import React from 'react';
import { Radiation, Package, Zap, Volume2, VolumeX, Coins } from 'lucide-react';
import { clsx } from 'clsx';
import { Stalker } from '@stalker/core';
import { EnergyBar, RadiationBar } from '#shared/ui/status-bars.js';
import { UserAvatar } from '#entities/user/ui/UserAvatar/index.js';
import { User } from '#shared/lib/platform/index.js';

interface PlayerHudProps {
  player: Stalker;
  user?: User;
  soundOn: boolean;
  isInventoryActive: boolean;
  onCityClick: () => void;
  onToggleSound: () => void;
  onInventoryClick: () => void;
}

export const PlayerHud: React.FC<PlayerHudProps> = ({
  player,
  user,
  soundOn,
  isInventoryActive,
  onCityClick,
  onToggleSound,
  onInventoryClick,
}) => {
  return (
    <header
      className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur-md flex flex-col terminal-card"
      style={{ paddingTop: 'calc(var(--safe-area-content-inset-top, env(safe-area-inset-top)) * 2)' }}
    >
      <div className="p-3 md:p-4 flex justify-between items-center max-w-6xl mx-auto w-full gap-2 border-b border-border/30 md:border-b-0">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="cursor-pointer" onClick={onCityClick}>
            <UserAvatar
              photoUrl={user?.photoUrl} name={user?.firstName} className="w-8
              h-8 md:w-10 md:h-10"
            />
          </div>
          <div className="min-w-0">
            <div className="text-[7px] md:text-[9px] uppercase tracking-[0.2em] text-muted-foreground font-bold truncate">
              {user?.username ? `@${user.username}` : 'STALKER ID'}
            </div>
            <div className="text-xs md:text-sm font-black tracking-tight uppercase text-foreground truncate">
              {user?.firstName || 'S.T.A.L.K.E.R.'}
            </div>
          </div>
        </div>

        <div className="flex gap-2 md:gap-6 items-center">
          <div className="hidden md:block w-32 space-y-1">
            <div className="flex justify-between text-[9px] uppercase font-bold tracking-wider">
              <span className="flex items-center gap-1 text-primary"><Zap size={10} /> Энергия</span>
              <span>{(player.energy * 100).toFixed(0)}%</span>
            </div>
            <EnergyBar current={player.energy} max={1} />
          </div>

          <div className="hidden md:block w-32 space-y-1">
            <div className="flex justify-between text-[9px] uppercase font-bold tracking-wider">
              <span className="flex items-center gap-1 text-accent"><Radiation size={10} /> Радиация</span>
              <span className="text-accent toxic-glow">{(player.radiation * 100).toFixed(1)}</span>
            </div>
            <RadiationBar current={player.radiation} />
          </div>

          <div className="flex flex-col items-end shrink-0">
            <div className="text-[7px] md:text-[9px] uppercase font-bold tracking-wider text-muted-foreground mb-0.5 md:mb-1">Баланс</div>
            <div className="flex items-center gap-1 md:gap-2 text-primary rust-glow font-bold">
              <Coins size={12} className="md:w-4 md:h-4 text-primary" />
              <span className="text-xs md:text-sm tracking-tighter">{player.money.toLocaleString()} ℟</span>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              onClick={onToggleSound}
              className={clsx(
                "p-1.5 md:p-2 rounded-sm border transition-all relative group",
                soundOn ? "border-accent/50 bg-accent/10 text-accent" : "border-border bg-input text-muted-foreground"
              )}
            >
              {soundOn ? <Volume2 size={16} className="md:w-5 md:h-5" /> : <VolumeX size={16} className="md:w-5 md:h-5" />}
            </button>

            <button
              onClick={onInventoryClick}
              className={clsx(
                "p-1.5 md:p-2 rounded-sm border transition-all relative group",
                isInventoryActive ? "border-primary bg-primary/20 text-primary" : "border-border bg-input text-foreground"
              )}
            >
              <Package size={16} className="md:w-5 md:h-5" />
              {player.inventory.length > 0 && (
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 md:w-4 md:h-4 bg-accent text-accent-foreground text-[8px] md:text-[10px] font-bold rounded-sm flex items-center justify-center">
                  {player.inventory.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Status Integration */}
      <div className="md:hidden px-4 py-2 flex justify-between gap-4 items-center bg-input/20">
        <div className="flex-1 space-y-1">
          <div className="flex justify-between text-[8px] font-black uppercase tracking-widest text-muted-foreground">
            <span className="flex items-center gap-1"><Zap size={8} /> E</span>
            <span>{Math.round(player.energy * 100)}%</span>
          </div>
          <EnergyBar current={player.energy} max={1.0} />
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex justify-between text-[8px] font-black uppercase tracking-widest text-muted-foreground">
            <span className="flex items-center gap-1"><Radiation size={8} /> R</span>
            <span className="text-accent">{(player.radiation * 100).toFixed(1)}</span>
          </div>
          <RadiationBar current={player.radiation} />
        </div>
      </div>
    </header>
  );
};
